import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Table,
  DatePicker,
  Select,
  Row,
  Col,
  Statistic,
  Typography,
  Progress,
  Button,
  Tabs,
  Tag,
  Divider,
  Space,
  Alert,
  Tooltip,
  Badge,
} from "antd";
import { Bar, Pie, Line } from "react-chartjs-2";
import {
  ClockCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
  WarningOutlined,
  RiseOutlined,
  FileTextOutlined,
  DownloadOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { AppContext } from "../context/AppContext";
import moment from "moment";
import { processDirectusLoan } from "../utils/loanUtils";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

// Enhanced aging buckets with more granular analysis
const AGING_BUCKETS = [
  { label: "Current (0-7 days)", days: 7, color: "#52c41a", priority: "low" },
  { label: "1-15 Days", days: 15, color: "#faad14", priority: "low" },
  { label: "16-30 Days", days: 30, color: "#fa8c16", priority: "medium" },
  { label: "31-60 Days", days: 60, color: "#ff4d4f", priority: "high" },
  { label: "61-90 Days", days: 90, color: "#a8071a", priority: "high" },
  { label: "91-180 Days", days: 180, color: "#722ed1", priority: "critical" },
  { label: "180+ Days", days: Infinity, color: "#000000", priority: "critical" },
];

// Calculate aging bucket for a loan
const calculateAgingBucket = (daysDelinquent) => {
  const days = Number(daysDelinquent) || 0;
  if (days <= 7) return "Current (0-7 days)";
  if (days <= 15) return "1-15 Days";
  if (days <= 30) return "16-30 Days";
  if (days <= 60) return "31-60 Days";
  if (days <= 90) return "61-90 Days";
  if (days <= 180) return "91-180 Days";
  return "180+ Days";
};

// Enhanced recovery prediction with aging consideration
const predictRecoveryWithAging = (loan) => {
  const daysDelinquent = Number(loan.days_delinquent) || 0;
  const creditScore = Number(loan.borrower.credit_score) || 650;
  const outstandingBalance = Number(loan.outstanding_balance) || 0;
  const agingBucket = calculateAgingBucket(daysDelinquent);

  // Aging-based risk multipliers
  const agingMultipliers = {
    "Current (0-7 days)": 1.0,
    "1-15 Days": 0.95,
    "16-30 Days": 0.85,
    "31-60 Days": 0.70,
    "61-90 Days": 0.50,
    "91-180 Days": 0.30,
    "180+ Days": 0.15,
  };

  // Base probability calculation
  const baseProbability = Math.min(95, Math.max(5, 
    (creditScore / 850) * 100 * 
    (loan.payment_history?.consistency || 0.5) * 
    (outstandingBalance < 50000 ? 1.1 : outstandingBalance < 100000 ? 1.0 : 0.9)
  ));

  // Apply aging multiplier
  const finalProbability = baseProbability * agingMultipliers[agingBucket];

  return {
    probability: Number(finalProbability.toFixed(1)),
    expectedRecovery: Number((outstandingBalance * (finalProbability / 100)).toFixed(2)),
    riskCategory: getRiskCategory(finalProbability),
    agingImpact: agingMultipliers[agingBucket],
  };
};

const getRiskCategory = (probability) => {
  if (probability >= 75) return "low";
  if (probability >= 50) return "medium";
  if (probability >= 25) return "high";
  return "critical";
};

const DebtAgeAnalysis = () => {
  const { company } = useContext(AppContext);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: [moment().subtract(6, 'months'), moment()],
    loanProduct: "all",
    branch: "all",
    agingBucket: "all",
  });

  const token = localStorage.getItem("token");

  const fetchLoans = async () => {
    try {
      setLoading(true);
      // Fetch all loans (not just delinquent ones for comprehensive aging analysis)
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/loans?fields=*,borrower.first_name,borrower.last_name,borrower.credit_score,loan_product.*,loan_transactions.*,branch.*&filter[company]=${company.id}&limit=-1`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Process and enrich loans for aging analysis
      const processed = response.data.data.map(processDirectusLoan);
      const enriched = processed.map((loan) => {
        const due = loan.next_payment_date || loan.due_date || loan.loan_enddate || loan.approval_date;
        const days = Number(loan.days_delinquent) || 0;
        const agingBucket = calculateAgingBucket(days);
        const recovery = predictRecoveryWithAging({ ...loan, agingBucket });
        
        return {
          ...loan,
          due_date: due,
          aging_bucket: agingBucket,
          recovery_prediction: recovery,
          is_delinquent: days > 0,
        };
      });

      setRawData(enriched);
    } catch (error) {
      console.error("Error fetching loans:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (company?.id) {
      fetchLoans();
    }
  }, [company]);

  // Filter data based on current filters
  const filteredData = rawData.filter((loan) => {
    if (filters.loanProduct !== "all" && loan.loan_product?.loan_name !== filters.loanProduct) {
      return false;
    }
    if (filters.branch !== "all" && loan.branch?.name !== filters.branch) {
      return false;
    }
    if (filters.agingBucket !== "all" && loan.aging_bucket !== filters.agingBucket) {
      return false;
    }
    return true;
  });

  // Calculate aging summary statistics
  const agingSummary = AGING_BUCKETS.map((bucket) => {
    const loans = filteredData.filter((loan) => loan.aging_bucket === bucket.label);
    const totalAmount = loans.reduce((sum, loan) => sum + loan.outstanding_balance, 0);
    const avgRecovery = loans.length > 0 
      ? loans.reduce((sum, loan) => sum + loan.recovery_prediction.probability, 0) / loans.length 
      : 0;
    
    return {
      ...bucket,
      count: loans.length,
      totalAmount,
      avgRecovery,
      percentage: filteredData.length > 0 ? (loans.length / filteredData.length) * 100 : 0,
    };
  });

  // Chart data for aging distribution
  const agingChartData = {
    labels: AGING_BUCKETS.map((b) => b.label),
    datasets: [
      {
        label: "Number of Loans",
        data: AGING_BUCKETS.map((bucket) => 
          filteredData.filter((loan) => loan.aging_bucket === bucket.label).length
        ),
        backgroundColor: AGING_BUCKETS.map((b) => b.color + "80"),
        borderColor: AGING_BUCKETS.map((b) => b.color),
        borderWidth: 2,
      },
      {
        label: "Amount Outstanding (ZMW)",
        data: AGING_BUCKETS.map((bucket) => 
          filteredData
            .filter((loan) => loan.aging_bucket === bucket.label)
            .reduce((sum, loan) => sum + loan.outstanding_balance, 0)
        ),
        backgroundColor: AGING_BUCKETS.map((b) => b.color + "40"),
        borderColor: AGING_BUCKETS.map((b) => b.color),
        borderWidth: 2,
        yAxisID: 'y1',
      },
    ],
  };

  // Pie chart for aging distribution
  const pieChartData = {
    labels: AGING_BUCKETS.map((b) => b.label),
    datasets: [
      {
        data: AGING_BUCKETS.map((bucket) => 
          filteredData.filter((loan) => loan.aging_bucket === bucket.label).length
        ),
        backgroundColor: AGING_BUCKETS.map((b) => b.color),
        borderWidth: 2,
        borderColor: '#fff',
      },
    ],
  };

  // Table columns for detailed view
  const columns = [
    {
      title: "Borrower",
      dataIndex: "borrower",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {record.borrower.first_name} {record.borrower.last_name}
          </div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Credit Score: {record.borrower.credit_score}
          </Text>
        </div>
      ),
      sorter: (a, b) => `${a.borrower.first_name} ${a.borrower.last_name}`.localeCompare(`${b.borrower.first_name} ${b.borrower.last_name}`),
    },
    {
      title: "Loan Details",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.loan_product?.loan_name}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            ID: {record.loan_number || record.id}
          </Text>
        </div>
      ),
    },
    {
      title: "Outstanding Balance",
      dataIndex: "outstanding_balance",
      render: (value) => (
        <Text strong style={{ color: '#1890ff' }}>
          ZMW {Number(value).toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => a.outstanding_balance - b.outstanding_balance,
    },
    {
      title: "Days Delinquent",
      dataIndex: "days_delinquent",
      render: (value) => {
        const days = Math.max(0, value);
        const bucket = calculateAgingBucket(days);
        const bucketConfig = AGING_BUCKETS.find(b => b.label === bucket);
        
        return (
          <Tooltip title={`Aging Bucket: ${bucket}`}>
            <Badge 
              count={days} 
              style={{ backgroundColor: bucketConfig?.color }}
              overflowCount={999}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => a.days_delinquent - b.days_delinquent,
    },
    {
      title: "Aging Bucket",
      dataIndex: "aging_bucket",
      render: (value) => {
        const bucketConfig = AGING_BUCKETS.find(b => b.label === value);
        return (
          <Tag color={bucketConfig?.color}>
            {value}
          </Tag>
        );
      },
      filters: AGING_BUCKETS.map(bucket => ({
        text: bucket.label,
        value: bucket.label,
      })),
      onFilter: (value, record) => record.aging_bucket === value,
    },
    {
      title: "Recovery Probability",
      render: (_, record) => {
        const prediction = record.recovery_prediction;
        return (
          <div>
            <Progress
              percent={prediction.probability}
              status={
                prediction.riskCategory === "low" ? "success" :
                prediction.riskCategory === "medium" ? "active" :
                prediction.riskCategory === "high" ? "exception" : "exception"
              }
              format={() => `${prediction.probability}%`}
              size="small"
            />
            <Text type="secondary" style={{ fontSize: '11px' }}>
              Expected: ZMW {prediction.expectedRecovery.toLocaleString()}
            </Text>
          </div>
        );
      },
    },
    {
      title: "Last Payment",
      dataIndex: "due_date",
      render: (value) => moment(value).format("DD MMM YYYY"),
      sorter: (a, b) => moment(a.due_date).unix() - moment(b.due_date).unix(),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button size="small" type="link">
            View Details
          </Button>
          <Button size="small" type="link">
            Contact
          </Button>
        </Space>
      ),
    },
  ];

  // Export functionality
  const exportToCSV = () => {
    if (!filteredData.length) {
      alert("No data to export");
      return;
    }

    const csvHeaders = [
      "Borrower Name",
      "Loan Product",
      "Loan Number",
      "Outstanding Balance",
      "Days Delinquent",
      "Aging Bucket",
      "Recovery Probability",
      "Expected Recovery",
      "Last Payment Date",
      "Credit Score"
    ].join(",");

    const csvRows = filteredData.map((loan) => {
      const prediction = loan.recovery_prediction;
      return [
        `"${loan.borrower.first_name} ${loan.borrower.last_name}"`,
        `"${loan.loan_product?.loan_name || 'N/A'}"`,
        `"${loan.loan_number || loan.id}"`,
        loan.outstanding_balance,
        loan.days_delinquent,
        `"${loan.aging_bucket}"`,
        `${prediction.probability}%`,
        prediction.expectedRecovery,
        `"${moment(loan.due_date).format("YYYY-MM-DD")}"`,
        loan.borrower.credit_score,
      ].join(",");
    });

    const csvContent = [csvHeaders, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = `debt-age-analysis-${moment().format("YYYYMMDD-HHmmss")}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Calculate key metrics
  const totalLoans = filteredData.length;
  const totalOutstanding = filteredData.reduce((sum, loan) => sum + loan.outstanding_balance, 0);
  const delinquentLoans = filteredData.filter(loan => loan.is_delinquent).length;
  const avgDaysDelinquent = delinquentLoans > 0 
    ? filteredData.filter(loan => loan.is_delinquent)
        .reduce((sum, loan) => sum + loan.days_delinquent, 0) / delinquentLoans 
    : 0;
  const totalExpectedRecovery = filteredData.reduce((sum, loan) => sum + loan.recovery_prediction.expectedRecovery, 0);

  return (
    <div style={{ padding: 24 }}>
      <Card bordered={false}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={3} style={{ marginBottom: 0 }}>
              <ClockCircleOutlined /> Debt Age Analysis
            </Title>
            <Text type="secondary">Comprehensive aging analysis of loan portfolio</Text>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchLoans}>
                Refresh
              </Button>
              <Button icon={<DownloadOutlined />} onClick={exportToCSV} type="primary">
                Export CSV
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Key Metrics */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Total Loans"
                value={totalLoans}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Total Outstanding"
                value={totalOutstanding}
                prefix={<DollarOutlined />}
                suffix="ZMW"
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Delinquent Loans"
                value={delinquentLoans}
                prefix={<WarningOutlined />}
                valueStyle={{ color: "#ff4d4f" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Expected Recovery"
                value={totalExpectedRecovery}
                prefix={<RiseOutlined />}
                suffix="ZMW"
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card title="Filters" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={6}>
              <RangePicker
                value={filters.dateRange}
                onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
                style={{ width: "100%" }}
                placeholder={["Start Date", "End Date"]}
              />
            </Col>
            <Col span={6}>
              <Select
                placeholder="Loan Product"
                style={{ width: "100%" }}
                value={filters.loanProduct}
                onChange={(value) => setFilters({ ...filters, loanProduct: value })}
              >
                <Select.Option value="all">All Products</Select.Option>
                {[...new Set(rawData.map((loan) => loan.loan_product?.loan_name))].map((name) => (
                  <Select.Option key={name} value={name}>
                    {name}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <Select
                placeholder="Aging Bucket"
                style={{ width: "100%" }}
                value={filters.agingBucket}
                onChange={(value) => setFilters({ ...filters, agingBucket: value })}
              >
                <Select.Option value="all">All Buckets</Select.Option>
                {AGING_BUCKETS.map((bucket) => (
                  <Select.Option key={bucket.label} value={bucket.label}>
                    {bucket.label}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <Select
                placeholder="Branch"
                style={{ width: "100%" }}
                value={filters.branch}
                onChange={(value) => setFilters({ ...filters, branch: value })}
              >
                <Select.Option value="all">All Branches</Select.Option>
                {[...new Set(rawData.map((loan) => loan.branch?.name))].map((name) => (
                  <Select.Option key={name} value={name}>
                    {name}
                  </Select.Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Aging Summary Table */}
        <Card title="Aging Summary" style={{ marginBottom: 24 }}>
          <Table
            dataSource={agingSummary}
            columns={[
              {
                title: "Aging Bucket",
                dataIndex: "label",
                render: (value, record) => (
                  <Tag color={record.color}>{value}</Tag>
                ),
              },
              {
                title: "Count",
                dataIndex: "count",
                render: (value) => <Badge count={value} style={{ backgroundColor: '#1890ff' }} />,
                sorter: (a, b) => a.count - b.count,
              },
              {
                title: "Percentage",
                dataIndex: "percentage",
                render: (value) => `${value.toFixed(1)}%`,
                sorter: (a, b) => a.percentage - b.percentage,
              },
              {
                title: "Total Amount",
                dataIndex: "totalAmount",
                render: (value) => `ZMW ${Number(value).toLocaleString()}`,
                sorter: (a, b) => a.totalAmount - b.totalAmount,
              },
              {
                title: "Avg Recovery %",
                dataIndex: "avgRecovery",
                render: (value) => (
                  <Progress 
                    percent={value} 
                    size="small" 
                    status={value > 70 ? "success" : value > 40 ? "active" : "exception"}
                  />
                ),
                sorter: (a, b) => a.avgRecovery - b.avgRecovery,
              },
            ]}
            pagination={false}
            size="small"
          />
        </Card>

        {/* Charts */}
        <Tabs defaultActiveKey="1" style={{ marginBottom: 24 }}>
          <TabPane tab="Aging Distribution" key="1">
            <Row gutter={16}>
              <Col span={12}>
                <Card title="Loan Count by Aging Bucket">
                  <Bar
                    data={agingChartData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { position: "top" },
                      },
                      scales: {
                        y: { beginAtZero: true },
                        y1: {
                          type: 'linear',
                          display: false,
                          position: 'right',
                        },
                      },
                    }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Aging Distribution (Pie Chart)">
                  <Pie
                    data={pieChartData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { position: "bottom" },
                      },
                    }}
                  />
                </Card>
              </Col>
            </Row>
          </TabPane>
          <TabPane tab="Recovery Analysis" key="2">
            <Row gutter={16}>
              <Col span={24}>
                <Card title="Recovery Probability by Aging Bucket">
                  <Bar
                    data={{
                      labels: AGING_BUCKETS.map((b) => b.label),
                      datasets: [
                        {
                          label: "Average Recovery Probability (%)",
                          data: AGING_BUCKETS.map((bucket) => {
                            const loans = filteredData.filter((loan) => loan.aging_bucket === bucket.label);
                            return loans.length > 0 
                              ? loans.reduce((sum, loan) => sum + loan.recovery_prediction.probability, 0) / loans.length
                              : 0;
                          }),
                          backgroundColor: AGING_BUCKETS.map((b) => b.color + "80"),
                          borderColor: AGING_BUCKETS.map((b) => b.color),
                          borderWidth: 2,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { position: "top" },
                      },
                      scales: {
                        y: { beginAtZero: true, max: 100 },
                      },
                    }}
                  />
                </Card>
              </Col>
            </Row>
          </TabPane>
        </Tabs>

        {/* Detailed Loan Table */}
        <Card 
          title="Detailed Loan Analysis" 
          extra={
            <Alert
              message={`Showing ${filteredData.length} of ${rawData.length} loans`}
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
            />
          }
        >
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            loading={loading}
            scroll={{ x: true }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} loans`,
            }}
            expandable={{
              expandedRowRender: (record) => (
                <div style={{ margin: 0 }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Text strong>Disbursement Date:</Text>{" "}
                      {moment(record.disbursement_date).format("DD MMM YYYY")}
                    </Col>
                    <Col span={8}>
                      <Text strong>Original Amount:</Text> ZMW {Number(record.amount).toLocaleString()}
                    </Col>
                    <Col span={8}>
                      <Text strong>Paid Amount:</Text> ZMW {Number(record.paid_amount || 0).toLocaleString()}
                    </Col>
                  </Row>
                  <Divider />
                  <Row gutter={16}>
                    <Col span={8}>
                      <Text strong>Payment Consistency:</Text>{" "}
                      {((record.payment_history?.consistency || 0) * 100).toFixed(1)}%
                    </Col>
                    <Col span={8}>
                      <Text strong>Total Payments:</Text> {record.payment_history?.total_payments || 0}
                    </Col>
                    <Col span={8}>
                      <Text strong>On-time Payments:</Text> {record.payment_history?.on_time_payments || 0}
                    </Col>
                  </Row>
                  <Divider />
                  <Space>
                    <Button type="link" size="small">
                      View Payment History
                    </Button>
                    <Button type="link" size="small">
                      Send Reminder
                    </Button>
                    <Button type="link" size="small">
                      Update Status
                    </Button>
                  </Space>
                </div>
              ),
            }}
          />
        </Card>
      </Card>
    </div>
  );
};

export default DebtAgeAnalysis;